import dayjs, { Dayjs } from 'dayjs';

// TypeScript port of LoadOne.Infrastructure.FiscalCalendar445 (current-period only).
// A 4-4-5 fiscal year is 12 periods of 4, 4, or 5 whole weeks, anchored to the
// Monday on-or-before Jan 1. 53-week years add the extra week to period 12.
// All dates are evaluated in America/New_York, matching the backend's EST helper.

const NY_TZ = 'America/New_York';
const PERIOD_WEEKS = [4, 4, 5, 4, 4, 5, 4, 4, 5, 4, 4, 5];
const TOTAL_WEEKS = PERIOD_WEEKS.reduce((a, b) => a + b, 0);

// Monday on-or-before Jan 1 of the given calendar year (NY, start of day).
const fiscalYearStartLocal = (year: number): Dayjs => {
  const jan1 = dayjs.tz(`${year}-01-01`, NY_TZ).startOf('day');
  // dayjs day(): Sunday=0..Saturday=6; anchor to Monday, mirroring the C# helper.
  const diff = (6 + jan1.day()) % 7;
  return jan1.subtract(diff, 'day');
};

const fiscalYearNumber = (date: Dayjs): number => {
  const year = date.year();
  if (date.isBefore(fiscalYearStartLocal(year))) return year - 1;
  if (date.isSameOrAfter(fiscalYearStartLocal(year + 1))) return year + 1;
  return year;
};

const extraWeekPeriodNumber = (fiscalYear: number): number => {
  const start = fiscalYearStartLocal(fiscalYear);
  const end = fiscalYearStartLocal(fiscalYear + 1);
  const totalWeeks = Math.round(end.diff(start, 'day') / 7);
  return totalWeeks > TOTAL_WEEKS ? 12 : 0;
};

/**
 * Start and exclusive end of the current 4-4-5 fiscal period (fiscal month), in NY time.
 */
export const getCurrentFiscalPeriod445 = (): { start: Dayjs; endExclusive: Dayjs } => {
  const now = dayjs().tz(NY_TZ).startOf('day');
  const fiscalYear = fiscalYearNumber(now);
  const extraWeekPeriod = extraWeekPeriodNumber(fiscalYear);

  let start = fiscalYearStartLocal(fiscalYear);
  for (let i = 0; i < PERIOD_WEEKS.length; i++) {
    const weeks = PERIOD_WEEKS[i] + (i + 1 === extraWeekPeriod ? 1 : 0);
    const end = start.add(weeks * 7, 'day');
    if (now.isBefore(end)) return { start, endExclusive: end };
    start = end;
  }

  return { start, endExclusive: start };
};
